import React, { useState, useEffect, useContext } from 'react';
import {
    TextField,
    MenuItem,
    Button,
    Select,
    FormControl,
    InputLabel,
    Box,
    Container,
    Typography,
    Card,
    Alert,
    Snackbar,
    CircularProgress,
    Grid,
    IconButton,
    ListItemText,
    ListItem,
    ListItemIcon, 
    List,
    LinearProgress,
    Chip,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isDate } from 'date-fns';
import es from 'date-fns/locale/es';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import CreateIcon from '@mui/icons-material/Create';
import { ContextSession } from "../../services/ContextSession";

// Función auxiliar para manejo seguro de fechas
const safeParseDate = (dateValue) => {
    if (!dateValue) return new Date();
    if (isDate(dateValue)) return dateValue;
    if (typeof dateValue === 'string') return parseISO(dateValue);
    return new Date(dateValue);
};

const PasesDeSalida = () => {
    // Estados para datos de la API
    const [conductores, setConductores] = useState([]);
    const [areas, setAreas] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [rutas, setRutas] = useState([]);
    const [loading, setLoading] = useState({
        conductores: true,
        areas: true,
        vehiculos: true,
        rutas: true
    });
    const [error, setError] = useState(null);

    // Estado del formulario
    const [formData, setFormData] = useState({
        area: '',
        conductor: '',
        vehiculo: '',
        fecha: new Date(),
        horaSalida: new Date(),
        motivo: '',
        observaciones_salida:'',
        ruta: '', 
        tipo_traslado:'',
        usuario_id: '',
        usuario_nombre: ''
    });
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [attachmentMetadata, setAttachmentMetadata] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null);
    const { sessionData } = useContext(ContextSession);

    // Estado para notificaciones
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Obtener usuario_id y nombre de la sesión
    useEffect(() => {
        if (sessionData) {
            const userInfo = getUserInfo();
            if (userInfo.id) {
                setFormData(prev => ({
                    ...prev,
                    usuario_id: userInfo.id.toString(),
                    usuario_nombre: userInfo.name
                }));
            }
        }
    }, [sessionData]);

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch conductores
                const conductoresResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/conductores/');
                if (!conductoresResponse.ok) throw new Error('Error al cargar conductores');
                const conductoresData = await conductoresResponse.json();
                setConductores(conductoresData);

                // Fetch areas
                const areasResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/areas/');
                if (!areasResponse.ok) throw new Error('Error al cargar áreas');
                const areasData = await areasResponse.json();
                setAreas(areasData);

                // Fetch vehículos
                const vehiculosResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/vehiculos/');
                if (!vehiculosResponse.ok) throw new Error('Error al cargar vehículos');
                const vehiculosData = await vehiculosResponse.json();
                setVehiculos(vehiculosData);

                // Fetch Rutas
                const rutasResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/rutas/');
                if (!rutasResponse.ok) throw new Error('Error al cargar rutas');
                const rutasData = await rutasResponse.json();
                setRutas(rutasData);

                setLoading({
                    conductores: false,
                    areas: false,
                    vehiculos: false,
                    rutas: false
                });

            } catch (err) {
                setError(err.message);
                showSnackbar(`Error: ${err.message}`, 'error');
                setLoading({
                    conductores: false,
                    areas: false,
                    vehiculos: false,
                    rutas: false
                });
            }
        };

        fetchData();
    }, []);

    // Función para obtener información del usuario
    const getUserInfo = (userData = sessionData) => {
        if (!userData) return { id: null, name: 'No identificado' };

        // Extraer ID del usuario
        const getId = (data) => 
            data.userId || data.user_id || data.id || data.usuario_id ||
            (data.user && data.user.id) || (data.usuario && data.usuario.id);

        // Extraer nombre del usuario
        const getName = (data) => {
            const name = 
                data.userName || data.username || data.nombre ||
                (data.user && data.user.name) || (data.usuario && data.usuario.nombre);
            
            // Si tenemos nombre y apellido, los combinamos
            if (data.nombre && data.apellido) {
                return `${data.nombre} ${data.apellido}`.trim();
            }
            
            return name || 'Usuario No Identificado';
        };

        return {
            id: getId(userData),
            name: getName(userData)
        };
    };

    // Filtrar conductores activos (solo los que están en estado 'Activo')
    const conductoresActivos = conductores.filter(conductor => 
        conductor.estado === 'Activo'
    );

    // Filtrar vehículos activos (solo los que están en estado 'Activo')
    const vehiculosActivos = vehiculos.filter(vehiculo => 
        vehiculo.estado === 'Activo'
    );

    // Función para obtener el texto del estado del conductor
    const getEstadoConductorTexto = (conductor) => {
        if (conductor.estado === 'Activo') return 'Activo';
        if (conductor.estado === 'en_ruta') return 'En Ruta';
        if (conductor.estado === 'Inactivo') return 'Inactivo';
        return conductor.estado;
    };

    // Función para obtener el color del chip según el estado del conductor
    const getColorEstadoConductor = (estado) => {
        switch (estado) {
            case 'Activo': return 'success';
            case 'en_ruta': return 'primary';
            case 'Inactivo': return 'error';
            default: return 'default';
        }
    };

    // Función para obtener el color del chip según el estado del vehículo
    const getColorEstadoVehiculo = (estado) => {
        switch (estado) {
            case 'Activo': return 'success';
            case 'en_ruta': return 'primary';
            case 'Inactivo': return 'error';
            case 'Mantenimiento': return 'warning';
            default: return 'default';
        }
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachmentFiles(prev => [...prev, ...files]);
        setAttachmentMetadata(prev => [...prev, ...files.map(f => ({ name: f.name, size: f.size }))]);
    };

    const handleRemoveAttachment = (index) => {
        setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
        setAttachmentMetadata(prev => prev.filter((_, i) => i !== index));
    };

    // Manejar cambios en fechas/horas
    const handleDateChange = (date, field) => {
        setFormData({
            ...formData,
            [field]: date,
        });
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Obtener usuario actual
        const userInfo = getUserInfo();
        
        // Validaciones básicas
        if (!formData.area || !formData.conductor || !formData.vehiculo || 
            !formData.motivo || !formData.tipo_traslado) {
            showSnackbar('Por favor complete todos los campos obligatorios', 'error');
            return;
        }

        // Validar que existe usuario_id
        if (!userInfo.id) {
            showSnackbar('No se pudo identificar al usuario. Por favor, inicie sesión nuevamente.', 'error');
            return;
        }

        // Validar que el conductor seleccionado está activo
        const conductorSeleccionado = conductores.find(c => c.id === parseInt(formData.conductor));
        if (conductorSeleccionado && conductorSeleccionado.estado !== 'Activo') {
            showSnackbar('El conductor seleccionado no está disponible. Por favor, seleccione otro conductor.', 'error');
            return;
        }

        // Validar que el vehículo seleccionado está activo
        const vehiculoSeleccionado = vehiculos.find(v => v.id === parseInt(formData.vehiculo));
        if (vehiculoSeleccionado && vehiculoSeleccionado.estado !== 'Activo') {
            showSnackbar('El vehículo seleccionado no está disponible. Por favor, seleccione otro vehículo.', 'error');
            return;
        }

        try {
            // 1. Preparar datos del pase 
            const paseData = {
                area: parseInt(formData.area),
                conductor: parseInt(formData.conductor),
                vehiculo: parseInt(formData.vehiculo),
                motivo: formData.motivo,
                tipo_traslado: formData.tipo_traslado,
                observaciones_finales: formData.observaciones_salida || "",
                ruta: formData.ruta ? parseInt(formData.ruta) : null,
                usuario_id: parseInt(userInfo.id), 
                usuario_nombre: userInfo.name 
            };

            console.log('Enviando paseData con usuario:', paseData);

            // 2. Crear el pase
            const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/pases/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(paseData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Error al crear el pase');
            }
            
            const result = await response.json();
            console.log('Pase creado exitosamente:', result);

            // 3. Subir archivos adjuntos si existen
            if (attachmentFiles.length > 0) {
                setUploadProgress({ completed: 0, total: attachmentFiles.length });

                for (let i = 0; i < attachmentFiles.length; i++) {
                    const formDataFile = new FormData();
                    formDataFile.append('archivo', attachmentFiles[i]);
                    formDataFile.append('descripcion', 'Adjunto inicial'); 
                    formDataFile.append('creado_por', userInfo.name || 'frontend');
                    formDataFile.append('nombre_archivo', attachmentFiles[i].name);

                    const adjuntoResp = await fetch(`https://bei360.injupemp.gob.hn:8444/api/pases/${result.id}/adjuntos/`, {
                        method: 'POST',
                        body: formDataFile
                    });

                    if (!adjuntoResp.ok) throw new Error(await adjuntoResp.text() || 'Error al subir adjunto');

                    // Actualizar progreso
                    setUploadProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
                }

                // Resetear estado de archivos después de subir
                setUploadProgress(null);
                setAttachmentFiles([]);
                setAttachmentMetadata([]);
            }

            // 4. Resetear formulario (mantener datos del usuario)
            setFormData({
                area: '',
                conductor: '',
                vehiculo: '',
                fecha: new Date(),
                horaSalida: new Date(),
                motivo: '',
                observaciones_salida: '',
                ruta: '', 
                tipo_traslado: '',
                usuario_id: userInfo.id ? userInfo.id.toString() : '',
                usuario_nombre: userInfo.name || ''
            });

            showSnackbar(`Pase creado exitosamente por ${userInfo.name}`, 'success');

        } catch (err) {
            console.error('Error completo:', err);
            showSnackbar(`Error: ${err.message}`, 'error');
        }
    };

    // Mostrar notificación
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
   
    // Cerrar notificación
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Obtener información del usuario para mostrar
    const userInfo = getUserInfo();

    // Mostrar loading mientras se cargan los datos
    if (loading.conductores || loading.areas || loading.vehiculos || loading.rutas) {
        return (
            <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Cargando datos...</Typography>
            </Container>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Container style={{ marginTop: 15 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">Registro de Pases de Salida</Typography>
                </div>
                 
                <Card sx={{ p: 3, mb: 3, width: '90%', alignItems: 'center', margin: '0 auto' }}>
                    <Typography variant="h6" gutterBottom>
                        Nuevo Pase de Salida
                    </Typography>
                    
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Columna 1 */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="area-label">Área Solicitante *</InputLabel>
                                    <Select
                                        labelId="area-label"
                                        id="area"
                                        name="area"
                                        value={formData.area}
                                        label="Área Solicitante *"
                                        onChange={handleChange}
                                        sx={{ mb: 2 }}
                                        required
                                    >
                                        {areas.map((area) => (
                                            <MenuItem key={area.id} value={area.id}>
                                                {area.nombre}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id="conductor-label">Conductor *</InputLabel>
                                    <Select
                                        labelId="conductor-label"
                                        id="conductor"
                                        name="conductor"
                                        value={formData.conductor}
                                        label="Conductor *"
                                        onChange={handleChange}
                                        sx={{ mb: 2 }}
                                        required
                                    >
                                        {conductores.map((conductor) => (
                                            <MenuItem 
                                                key={conductor.id} 
                                                value={conductor.id}
                                                disabled={conductor.estado !== 'Activo'}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                    <Box>
                                                        {conductor.nombre} {conductor.apellido} 
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Licencia: {conductor.tipo_licencia}
                                                        </Typography>
                                                    </Box>
                                                    <Chip 
                                                        label={getEstadoConductorTexto(conductor)}
                                                        color={getColorEstadoConductor(conductor.estado)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {conductoresActivos.length === 0 && (
                                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                            No hay conductores activos disponibles
                                        </Typography>
                                    )}
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id="tipo-traslado-label">Tipo de Traslado *</InputLabel>
                                    <Select
                                        labelId="tipo-traslado-label"
                                        id="tipo_traslado"
                                        name="tipo_traslado"
                                        value={formData.tipo_traslado}
                                        label="Tipo de Traslado *"
                                        onChange={handleChange}
                                        sx={{ mb: 2 }}
                                        required
                                    >
                                        <MenuItem value="Gira">Gira</MenuItem>
                                        <MenuItem value="Local">Local</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Nuevo campo para Ruta (opcional) */}
                                <FormControl fullWidth>
                                    <InputLabel id="ruta-label">Ruta</InputLabel>
                                    <Select
                                        labelId="ruta-label"
                                        id="ruta"
                                        name="ruta"
                                        value={formData.ruta}
                                        label="Ruta"
                                        onChange={handleChange}
                                        sx={{ mb: 2 }}
                                    >
                                        <MenuItem value="">Seleccione una ruta...</MenuItem>
                                        {rutas.map((ruta) => (
                                            <MenuItem key={ruta.id} value={ruta.id}>
                                                {ruta.nombre} - {ruta.descripcion}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Columna 2 */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="vehiculo-label">Vehículo *</InputLabel>
                                    <Select
                                        labelId="vehiculo-label"
                                        id="vehiculo"
                                        name="vehiculo"
                                        value={formData.vehiculo}
                                        label="Vehículo *"
                                        onChange={handleChange}
                                        sx={{ mb: 2 }}
                                        required
                                    >
                                        {vehiculos.map((vehiculo) => (
                                            <MenuItem
                                                key={vehiculo.id}
                                                value={vehiculo.id}
                                                disabled={vehiculo.estado !== 'Activo'}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                    <Box>
                                                        {vehiculo.placa} ({vehiculo.marca} {vehiculo.modelo} {vehiculo.color} {vehiculo.vin})
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Kilometraje: {vehiculo.kilometraje} km
                                                        </Typography>
                                                    </Box>
                                                    <Chip 
                                                        label={vehiculo.estado}
                                                        color={getColorEstadoVehiculo(vehiculo.estado)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {vehiculosActivos.length === 0 && (
                                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                            No hay vehículos activos disponibles
                                        </Typography>
                                    )}
                                </FormControl>

                                <TextField
                                    label="Fecha de Salida"
                                    name="fecha"
                                    value={format(safeParseDate(formData.fecha), 'yyyy-MM-dd')}
                                    onChange={(e) => handleDateChange(e.target.value, 'fecha')}
                                    fullWidth
                                    required
                                    type="date"
                                    sx={{ mb: 2 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                 <TextField
                                    label="Motivo de Salida"
                                    name="motivo"
                                    value={formData.motivo}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                 />
                                <TextField
                                    label="Observaciones"
                                    name="observaciones_salida"
                                    value={formData.observaciones_salida}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Observaciones adicionales..."
                                />
                            </Grid>
                        </Grid>

                        {/* Sección de adjuntos */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Adjuntos
                            </Typography>
                            <input
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                id="raised-button-file"
                                multiple
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="raised-button-file">
                                <Button 
                                    variant="contained" 
                                    color="success"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Subir Archivos
                                </Button>
                            </label>
                            
                            {uploadProgress && (
                                <Box mt={2}>
                                    <Typography variant="body2">
                                        Subiendo archivos: {uploadProgress.completed} de {uploadProgress.total}
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={(uploadProgress.completed / uploadProgress.total) * 100} 
                                    />
                                </Box>
                            )}
                            
                            {attachmentMetadata.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle2">
                                        Archivos seleccionados:
                                    </Typography>
                                    <List dense>
                                        {attachmentMetadata.map((file, index) => (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <CloudUploadIcon/>
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={file.name} 
                                                    secondary={`${(file.size / 1024).toFixed(2)} KB`} 
                                                />
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={() => handleRemoveAttachment(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Grid>

                        {/* Botón de submit */}
                        <Box sx={{ mt: 4, textAlign: 'right' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<CreateIcon />}
                                sx={{ borderRadius: 2, px: 4, py: 1, backgroundColor: "#19438C" }}
                                disabled={!userInfo.id || conductoresActivos.length === 0 || vehiculosActivos.length === 0}
                            >
                                {userInfo.id ? `Crear Pase (${userInfo.name})` : 'Espere...'}
                            </Button>
                            {(conductoresActivos.length === 0 || vehiculosActivos.length === 0) && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {conductoresActivos.length === 0 && vehiculosActivos.length === 0 
                                        ? 'No se puede crear pase: no hay conductores ni vehículos activos disponibles'
                                        : conductoresActivos.length === 0 
                                            ? 'No se puede crear pase: no hay conductores activos disponibles'
                                            : 'No se puede crear pase: no hay vehículos activos disponibles'
                                    }
                                </Typography>
                            )}
                        </Box>
                    </form>
                </Card>

                {/* Notificación */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </LocalizationProvider>
    );
};

export default PasesDeSalida;